import "@/lib/prism-setup";
import { CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  type ElementTransformer,
  HEADING,
  TRANSFORMERS,
} from "@lexical/markdown";
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $createHeadingNode, $isHeadingNode, HeadingNode, QuoteNode } from "@lexical/rich-text";
import type { AIUsageStats } from "@repo/shared/types";
import { $getRoot, EditorState } from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { ContentEditable } from "@/components/editor/editor-ui/content-editable";
import { FLOW_MARKDOWN_HEADING_CLASS } from "@/components/ui/flow-component-header";
import { log } from "@/lib/logger/browser-logger";
import { cn } from "@/lib/utils";
import {
  type MarkdownEditorHeadings,
  SINGLE_HEADING_TAG,
  ToolbarPlugin,
} from "./markdown-editor-with-toolbar";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  showCharacterCount?: boolean;
  showSpeechToText?: boolean;
  showAI?: boolean;
  /** `levels` offers H1–H3; `single` offers the one flow-page heading (QL-296). */
  headings?: MarkdownEditorHeadings;
  editable?: boolean;
  aiContext?: {
    flowName: string;
    clientName: string;
    pageName: string;
    riskScore?: number;
    language?: string;
  };
}

// QL-296 — the `single` mode's "# " shortcut. Any number of hashes creates the
// one heading the flow page has (SINGLE_HEADING_TAG), so a designer typing "#"
// or "##" out of habit still gets the same block. This transformer only drives
// MarkdownShortcutPlugin, i.e. typing: load and save go through
// MarkdownSyncPlugin with the stock TRANSFORMERS, whose HEADING export is what
// keeps a legacy `#`/`##` at its level. The `export` below is required by the
// type and mirrors the stock one; changing it would not change what is saved.
const SINGLE_HEADING: ElementTransformer = {
  dependencies: [HeadingNode],
  export: (node, exportChildren) => {
    if (!$isHeadingNode(node)) return null;
    const level = Number(node.getTag().slice(1));
    return `${"#".repeat(level)} ${exportChildren(node)}`;
  },
  regExp: /^(#{1,6})\s/,
  replace: (parentNode, children, _match, isImport) => {
    const node = $createHeadingNode(SINGLE_HEADING_TAG);
    node.append(...children);
    parentNode.replace(node);
    if (!isImport) node.select(0, 0);
  },
  type: "element",
};

const SINGLE_HEADING_TRANSFORMERS = TRANSFORMERS.map((transformer) =>
  transformer === HEADING ? SINGLE_HEADING : transformer,
);

// Plugin to sync markdown value with editor state
function MarkdownSyncPlugin({
  markdown,
  onMarkdownChange,
}: {
  markdown: string;
  onMarkdownChange: (md: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const isInitialMount = useRef(true);

  // Initialize editor with markdown on mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (markdown) {
        editor.update(() => {
          $convertFromMarkdownString(markdown, TRANSFORMERS);
        });
      }
    }
  }, [editor, markdown]);

  // Convert editor state to markdown on changes
  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const md = $convertToMarkdownString(TRANSFORMERS);
        onMarkdownChange(md);
      });
    },
    [onMarkdownChange],
  );

  return <OnChangePlugin ignoreSelectionChange={false} onChange={handleChange} />;
}

// Character counter plugin with AI stats
function CharacterCountPlugin({ aiStats }: { aiStats: AIUsageStats | null }) {
  const [editor] = useLexicalComposerContext();
  const [characterCount, setCharacterCount] = useState(0);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const text = root.getTextContent();
        setCharacterCount(text.length);
      });
    });
  }, [editor]);

  return (
    <div className="px-4 py-2 border-t text-xs text-muted-foreground flex items-center justify-between">
      <div>
        {characterCount} character{characterCount !== 1 ? "s" : ""}
      </div>
      {aiStats && (
        <div className="flex items-center gap-3">
          <span>{aiStats.timing}ms</span>
          <span className="text-muted-foreground/70">-</span>
          <span>{aiStats.tokens.total} tokens</span>
          <span className="text-muted-foreground/70">-</span>
          <span>€{aiStats.cost.toFixed(5)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * `editable` reaches Lexical through `initialConfig`, which the composer reads
 * once when it mounts. Anything that flips the prop afterwards — unlocking a
 * submitted flow instance for editing being the live case — updates the React
 * chrome around the editor (toolbar, character count, opacity) while leaving the
 * editor itself read-only, so push the change down explicitly.
 */
function EditableSyncPlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

export function LexicalMarkdownEditor({
  value,
  onChange,
  placeholder = "Start typing...",
  className,
  showCharacterCount = false,
  showSpeechToText = false,
  showAI = true,
  headings = "levels",
  editable = true,
  aiContext,
}: MarkdownEditorProps) {
  const [aiStats, setAIStats] = useState<AIUsageStats | null>(null);
  const singleHeading = headings === "single";
  // Shortcuts only; the sync plugin imports/exports with the full set either way.
  const shortcutTransformers = singleHeading ? SINGLE_HEADING_TRANSFORMERS : TRANSFORMERS;

  const editorConfig: InitialConfigType = {
    namespace: "MarkdownEditor",
    editable,
    theme: {
      // Single mode mirrors FlowMarkdown's paragraph (`my-2`) as well as its
      // heading class, so the gap from a heading to the paragraph below it is
      // the same in the editor as on the flow page.
      paragraph: singleHeading ? "my-2" : "mb-2",
      // Single mode draws every level with the class FlowMarkdown renders, so
      // the editor shows the flow page's one heading whatever level the stored
      // markdown carries.
      heading: singleHeading
        ? {
            h1: FLOW_MARKDOWN_HEADING_CLASS,
            h2: FLOW_MARKDOWN_HEADING_CLASS,
            h3: FLOW_MARKDOWN_HEADING_CLASS,
            h4: FLOW_MARKDOWN_HEADING_CLASS,
            h5: FLOW_MARKDOWN_HEADING_CLASS,
            h6: FLOW_MARKDOWN_HEADING_CLASS,
          }
        : {
            h1: "text-2xl font-bold mt-6 mb-3",
            h2: "text-xl font-bold mt-4 mb-2",
            h3: "text-lg font-bold mt-3 mb-2",
          },
      list: {
        ul: "list-disc list-outside ml-5 my-2",
        ol: "list-decimal list-outside ml-5 my-2",
        listitem: "my-1",
      },
      quote: "border-l-4 border-border pl-4 italic my-2",
      code: "bg-muted rounded px-1 py-0.5 font-mono text-sm",
      text: {
        bold: "font-bold",
        italic: "italic",
        underline: "underline",
      },
    },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, CodeNode, LinkNode],
    onError: (err: Error) => {
      log.error({ err }, "lexical editor error");
    },
  };

  return (
    <div className={className}>
      <div
        className={cn(
          // Design 14k — focus and error live on this outer chassis, never on the
          // inner content area; a ring drawn inside the border reads as a
          // second, nested field.
          "overflow-hidden rounded-md border border-input bg-card shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
          !editable && "opacity-75",
        )}
      >
        <LexicalComposer initialConfig={editorConfig}>
          <div>
            {editable && (
              <ToolbarPlugin
                showSpeechToText={showSpeechToText}
                showAI={showAI}
                headings={headings}
                onAIStatsUpdate={setAIStats}
                aiContext={aiContext}
              />
            )}
            <div className="relative">
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    placeholder={placeholder}
                    className="prose block max-h-[400px] min-h-[90px] max-w-none overflow-auto p-3 text-sm leading-normal focus:outline-none dark:prose-invert"
                    placeholderClassName="pointer-events-none absolute left-0 top-0 select-none overflow-hidden text-ellipsis p-3 text-sm leading-normal text-muted-foreground"
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </div>
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
            <MarkdownShortcutPlugin transformers={shortcutTransformers} />
            <MarkdownSyncPlugin markdown={value} onMarkdownChange={onChange} />
            <EditableSyncPlugin editable={editable} />
            {showCharacterCount && <CharacterCountPlugin aiStats={aiStats} />}
          </div>
        </LexicalComposer>
      </div>
    </div>
  );
}
